/* Fetch method is based on https://strapi.io/blog/build-a-blog-with-next-react-js-strapi */

import { merge } from 'lodash'
import qs from 'qs'

import { StrapiCollectionResponse, StrapiSingleResponse } from './types/strapi-content'
import { camelize } from '../../utils/camelize'
import { STRAPI_API_URL } from 'config'

type ApiParams = {
  populate?: '*' | object | string[]
  sort?: string[]
  filters?: any
  pagination?: {
    pageSize?: number
    page?: number
  }
  publicationState: 'live'
}

export const getPublicationParams = (): ApiParams => {
  return { publicationState: 'live' }
}

export class StrapiClient {
  private readonly defaultOptions: RequestInit

  constructor(options: RequestInit = {}) {
    this.defaultOptions = merge(options, {
      headers: {
        'Content-Type': 'application/json',
      },
    })
  }

  /**
   * Helper to make GET requests to Strapi API endpoints
   * @param {string} path Path of the API route
   * @param {Object} urlParamsObject URL params object, will be stringified
   * @param {Object} options Options passed to fetch
   * @param {boolean} exactPath Should `/api` **not** be prepended to the request path
   * @returns Parsed API call response
   */
  fetch = async <T>(
    path: string,
    urlParamsObject = {},
    options = {},
    exactPath?: boolean
  ) => {
    return this.fetchDefault<T>(path, urlParamsObject, options, exactPath)
  }

  private fetchDefault = async <T>(
    path: string,
    urlParamsObject = {},
    options = {},
    exactPath?: boolean
  ) => {
    const mergedOptions: RequestInit = merge(this.defaultOptions, options)

    const queryString = qs.stringify({
      ...getPublicationParams(),
      ...urlParamsObject,
    })
    const requestUrl = `${STRAPI_API_URL}${exactPath ? '' : '/api'}${path}${
      queryString ? `?${queryString}` : ''
    }`

    const response = await fetch(requestUrl, mergedOptions)

    if (!response.ok) {
      const text = await response.text()
      console.error(text)
      throw new Error(text)
    }

    const data = await response.json()

    if (data.error) {
      const { message } = data.error
      console.error(message)
      throw new Error(message)
    }

    return camelize<T>(data)
  }

  getSingle = <T>(path: string, params?: ApiParams): Promise<StrapiSingleResponse<T>> => {
    return this.fetch(path, params, { method: 'GET' }) as Promise<StrapiSingleResponse<T>>
  }

  getSingleBySlug = async <T>(
    path: string,
    slug: string,
    params?: ApiParams
  ): Promise<StrapiSingleResponse<T> | null> => {
    const response = (await this.fetch(
      path,
      {
        ...params,
        filters: {
          slug,
        },
        pagination: {
          pageSize: 1,
        },
      },
      { method: 'GET' }
    )) as StrapiCollectionResponse<T>

    const result = response?.data?.[0]

    if (!result) {
      return null
    }

    return {
      data: result,
      meta: response.meta,
    }
  }

  getCollection = <T>(
    path: string,
    params?: ApiParams
  ): Promise<StrapiCollectionResponse<T>> => {
    return this.fetch(path, params, { method: 'GET' }) as Promise<
      StrapiCollectionResponse<T>
    >
  }
}

export const Strapi = new StrapiClient()

export const getStrapiClient = async () => {
  return new StrapiClient({})
}
